// HeaderActions — the trailing right-side controls in the top header: theme
// toggle (☾/☀) and settings (⚙). Shared between Layout (navigator + settings
// routes) and PaperReview (which renders its own contextual header).

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Tooltip } from "./Tooltip";
import { trackEvent } from "../telemetry/useTelemetry";
import {
  getStoredTheme,
  onStoredThemeChange,
  setStoredTheme,
  type Theme,
} from "../theme";

interface Props {
  onSettingsOpen: () => void;
}

export function HeaderActions({ onSettingsOpen }: Props) {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<Theme>(() => getStoredTheme());

  useEffect(() => onStoredThemeChange(setTheme), []);

  function toggleTheme() {
    const next: Theme = theme === "light" ? "dark" : "light";
    setTheme(next);
    setStoredTheme(next);
  }

  const nextModeKey: "header_theme_dark" | "header_theme_light" =
    theme === "light" ? "header_theme_dark" : "header_theme_light";

  return (
    <>
      <Tooltip tipKey={nextModeKey}>
        <button
          type="button"
          className="app-header__btn app-header__btn--ghost"
          onClick={toggleTheme}
          aria-label={t(`tooltip.${nextModeKey}`)}
        >
          {theme === "light" ? "☾" : "☀"}
        </button>
      </Tooltip>

      <Tooltip tipKey="header_settings">
        <button
          type="button"
          className="app-header__btn app-header__btn--ghost"
          onClick={() => {
            trackEvent("settings.open");
            onSettingsOpen();
          }}
          aria-label={t("header.settings")}
        >
          ⚙
        </button>
      </Tooltip>
    </>
  );
}
